import { useCallback, useRef, useEffect } from 'react';

import {
  useWebSocketSubscription,
  useWebSocketStatus,
} from '@/contexts/WebSocketContext';
import { useAlarmEventStore } from '@/stores/alarmEventStore';
import type { AlarmEventEnvelope, LiveAlarmEvent } from '@/types/alarm';

interface UseAlarmWebSocketOptions {
  onAlarmReceived?: (event: AlarmEventEnvelope) => void;
}

export function useAlarmWebSocket(options: UseAlarmWebSocketOptions = {}) {
  const { onAlarmReceived } = options;
  const addEvent = useAlarmEventStore(s => s.addEvent);
  const status = useWebSocketStatus();
  const callbackRef = useRef(onAlarmReceived);

  useEffect(() => {
    callbackRef.current = onAlarmReceived;
  }, [onAlarmReceived]);

  const handleAlarmEvent = useCallback(
    (event: AlarmEventEnvelope) => {
      const liveEvent: LiveAlarmEvent = {
        ...event,
        id: `alarm-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        arrived_at: Date.now(),
      };
      addEvent(liveEvent);
      callbackRef.current?.(event);
    },
    [addEvent]
  );

  useWebSocketSubscription<AlarmEventEnvelope>(
    'alarm_event',
    handleAlarmEvent,
    status?.isConnected ?? false
  );

  return {
    isConnected: status?.isConnected ?? false,
    connectionStatus: status?.status ?? 'disconnected',
  };
}
